import { useEffect, useRef } from "react";

// A brief curtain of Aurora-Borealis light painted behind the cursor: five
// wide, translucent, laterally-waving layers — vivid violet, strong blue,
// supporting cyan, true green, and a soft violet fringe — kept colour-distinct
// rather than blending to teal, with organic edge variation and no defined
// centre line. Widens and disperses toward the tail, fading over ~0.5s.
// Decorative only: pointer-events none, screen blend, real cursor untouched.
// Skips touch + prefers-reduced-motion.
const LIFETIME = 500;          // ms a point lingers before it fully fades
const BASE_WIDTH = 30;         // px base half-scale of the curtain (2–3x prior)
const SCALE = 0.6;             // internal render scale — keeps blur buttery
const PALETTE = [
  [168, 85, 247],  // vivid violet
  [59, 130, 246],  // strong blue
  [34, 211, 238],  // supporting cyan
  [52, 211, 153],  // true green
  [45, 212, 191],  // teal (blend bridge back to violet)
];

const sample = (f) => {
  f = ((f % 1) + 1) % 1;
  const s = f * PALETTE.length;
  const i = Math.floor(s);
  const t = s - i;
  const a = PALETTE[i % PALETTE.length];
  const b = PALETTE[(i + 1) % PALETTE.length];
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
};

// Overlapping curtain layers — each hero colour gets its own dedicated layer
// (c = exact palette fraction) PLUS a lateral bias, so the colours sit in
// separate bands across the curtain width like a real aurora cross-section:
// violet fringe → violet → blue → cyan → green.
const LAYERS = [
  { c: 0.00, wm: 1.15, am: 0.130, blur: 18, amp: 10, freq: 1.3, ph: 0.0, ww: 0.28, bias: -34 }, // violet
  { c: 0.20, wm: 0.95, am: 0.120, blur: 16, amp: 18, freq: 1.7, ph: 1.3, ww: 0.32, bias: -16 }, // blue
  { c: 0.40, wm: 1.05, am: 0.090, blur: 20, amp: 24, freq: 1.1, ph: 2.6, ww: 0.26, bias: 2 },   // cyan support
  { c: 0.60, wm: 0.90, am: 0.120, blur: 16, amp: 14, freq: 2.1, ph: 3.7, ww: 0.34, bias: 20 },  // green
  { c: 0.99, wm: 0.80, am: 0.080, blur: 22, amp: 28, freq: 0.9, ph: 5.0, ww: 0.30, bias: -52 }, // violet fringe
];

export const AuroraCursor = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    if (window.matchMedia("(hover: none)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = canvas.getContext("2d");
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let pts = [];
    let raf;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2) * SCALE;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const onMove = (e) => {
      const now = performance.now();
      const last = pts[pts.length - 1];
      if (last && Math.hypot(e.clientX - last.x, e.clientY - last.y) < 2) return;
      pts.push({ x: e.clientX, y: e.clientY, t: now });
      if (pts.length > 400) pts.shift();
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("resize", resize);

    // Catmull-Rom smoothed, densely-resampled centreline with interpolated time.
    const smooth = (p) => {
      if (p.length < 3) return p.slice();
      const out = [];
      for (let i = 0; i < p.length - 1; i++) {
        const p0 = p[i - 1] || p[i];
        const p1 = p[i];
        const p2 = p[i + 1];
        const p3 = p[i + 2] || p2;
        const seg = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        const steps = Math.max(2, Math.min(24, Math.round(seg / 3)));
        for (let s = 0; s < steps; s++) {
          const t = s / steps;
          const t2 = t * t;
          const t3 = t2 * t;
          out.push({
            x: 0.5 * ((2 * p1.x) + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
            y: 0.5 * ((2 * p1.y) + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
            t: p1.t + (p2.t - p1.t) * t,
          });
        }
      }
      out.push({ ...p[p.length - 1] });
      return out;
    };

    const lifeAt = (pt, now) => {
      const a = (now - pt.t) / LIFETIME;
      return a < 0 ? 1 : a > 1 ? 0 : 1 - a;
    };

    const drawLayer = (S, now, L) => {
      const n = S.length;
      if (n < 2) return;
      const left = [];
      const right = [];
      for (let i = 0; i < n; i++) {
        const prev = S[Math.max(0, i - 1)];
        const next = S[Math.min(n - 1, i + 1)];
        let nx = -(next.y - prev.y);
        let ny = next.x - prev.x;
        const len = Math.hypot(nx, ny) || 1;
        nx /= len; ny /= len;
        const pos = i / (n - 1);                       // 0 tail(old) -> 1 head(new)
        // lateral drift so layers don't share a centre line (wavy curtains),
        // plus a per-layer bias that bands the colours across the width
        const lateral = L.amp * Math.sin(pos * L.freq * 6.283 + L.ph + now * 0.0006) + L.bias;
        // wider + more dispersed toward the tail; organic width wave along path
        const disperse = 1 + (1 - pos) * 0.9;
        const widthWave = 1 + L.ww * Math.sin(pos * 12.566 + L.ph * 1.7 + now * 0.001);
        const w = BASE_WIDTH * L.wm * disperse * (0.3 + 0.7 * Math.pow(pos, 0.6)) * widthWave * 0.5;
        const cx = S[i].x + nx * lateral;
        const cy = S[i].y + ny * lateral;
        left.push([cx + nx * w, cy + ny * w]);
        right.push([cx - nx * w, cy - ny * w]);
      }
      ctx.beginPath();
      ctx.moveTo(left[0][0], left[0][1]);
      for (let i = 1; i < n; i++) ctx.lineTo(left[i][0], left[i][1]);
      for (let i = n - 1; i >= 0; i--) ctx.lineTo(right[i][0], right[i][1]);
      ctx.closePath();

      const grad = ctx.createLinearGradient(S[0].x, S[0].y, S[n - 1].x, S[n - 1].y);
      const STOPS = 14;
      for (let m = 0; m <= STOPS; m++) {
        const f = m / STOPS;
        const idx = Math.min(n - 1, Math.round(f * (n - 1)));
        const life = lifeAt(S[idx], now);
        const a = L.am * 2.4 * Math.pow(life, 1.5) * (0.08 + 0.92 * f); // fades along whole length
        const [r, g, b] = sample(L.c + now * 0.000004);
        grad.addColorStop(f, `rgba(${r | 0}, ${g | 0}, ${b | 0}, ${a.toFixed(3)})`);
      }
      ctx.filter = `blur(${L.blur * SCALE}px)`;
      ctx.fillStyle = grad;
      ctx.fill();
    };

    const loop = () => {
      const now = performance.now();
      pts = pts.filter((p) => now - p.t < LIFETIME);
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      if (pts.length > 1) {
        const S = smooth(pts);
        ctx.globalCompositeOperation = "lighter"; // additive — hues stay distinct
        for (const L of LAYERS) drawLayer(S, now, L);
        ctx.globalCompositeOperation = "source-over";
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className="aurora-trail" aria-hidden="true" />;
};
